import {
  ApplicationConfig,
  LOCALE_ID,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter, withInMemoryScrolling, withNavigationErrorHandler } from '@angular/router';
import { provideZoneChangeDetection } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';

import { routes } from './app.routes';
import { initialAngularLocaleId } from './core/i18n/angular-locale';
import { LocalizationModule } from './core/i18n/localization.module';
import { Translation } from './core/i18n/translation.service';
import { authRefreshInterceptor } from './core/interceptors/auth-refresh.interceptor';
import { handleCommonResponseInterceptor } from './core/interceptors/handle-common-response.interceptor';
import { handleErrorsInterceptor } from './core/interceptors/handle-errors.interceptor';
import { handleRequestHeaderInterceptor } from './core/interceptors/handle-request-header.interceptor';
import { recoverLazyRouteNavigationError } from './core/router/lazy-route-recovery';
import WorkNestPreset from './core/theme/worknest-preset';
import { provideTruncationTooltips } from './shared/utils/truncation-tooltip';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection(),
    provideAnimations(),
    provideRouter(
      routes,
      withNavigationErrorHandler(recoverLazyRouteNavigationError),
      // Landing-page section links (`/#modules`, `/#demo`) and back/forward restore.
      withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' }),
    ),
    importProvidersFrom(LocalizationModule.forRoot()),
    // Locale for Angular's date/number pipes: Arabic month names in the Arabic UI
    // (digits stay Western). See core/i18n/angular-locale.ts.
    { provide: LOCALE_ID, useFactory: initialAngularLocaleId },
    // Block Angular's "stable"/initialized state (both in the browser and during
    // build-time prerendering) until the current language's translations have
    // actually loaded. Without this, the prerenderer can snapshot the DOM before
    // the (fire-and-forget) translation fetch resolves, shipping raw i18n keys
    // instead of real text — this was a non-deterministic race, not a fix.
    provideAppInitializer(() => inject(Translation).preloadCurrentLanguage()),
    // WN-946: full text of ellipsized table cells on hover.
    provideTruncationTooltips(),
    provideHttpClient(
      withInterceptors([
        handleRequestHeaderInterceptor,
        handleErrorsInterceptor,
        authRefreshInterceptor,
        // Must run before auth refresh on responses so WorkNest envelope statusCode 401 is handled like HTTP 401.
        handleCommonResponseInterceptor,
      ]),
    ),
    ConfirmationService,
    MessageService,
    providePrimeNG({
      overlayOptions: {
        appendTo: 'body',
      },
      theme: {
        preset: WorkNestPreset,
        options: {
          cssLayer: {
            name: 'primeng',
            order: 'base, primeng, components, utilities',
          },
          defaultIconSet: 'primeicons',
          darkModeSelector: '.wn-dark',
        },
      },
    }), provideClientHydration(withEventReplay()),
  ],
};
